import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";

function flatten(nodes, depth, out, expanded) {
  for (const n of nodes) {
    out.push({ ...n, depth });
    if (expanded[n.id] && n.reports?.length) {
      flatten(n.reports, depth + 1, out, expanded);
    }
  }
}

export default function OrgChart() {
  const [tree, setTree] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/org-chart").then((r) => {
      setTree(r.data);
      // open first two levels by default
      const open = {};
      const walk = (arr, d) => arr.forEach((n) => {
        if (d < 2) open[n.id] = true;
        if (n.reports) walk(n.reports, d + 1);
      });
      walk(r.data, 0);
      setExpanded(open);
    });
  }, []);

  if (!tree) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const rows = [];
  flatten(tree, 0, rows, expanded);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-6" data-testid="org-chart-page">
      <header>
        <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Hierarchy</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Organisation Chart</h1>
        <p className="text-stone-600 mt-1 text-sm">Reporting structure across teams.</p>
      </header>

      <div className="space-y-2" data-testid="org-tree">
        {rows.length === 0 && <div className="text-sm text-stone-500">No hierarchy data.</div>}
        {rows.map((n) => {
          const hasReports = n.reports && n.reports.length > 0;
          const open = !!expanded[n.id];
          return (
            <div key={n.id} className="card-flat flex items-center gap-3"
                 style={{ marginLeft: n.depth * 28, padding: 12 }}
                 data-testid={`org-node-${n.id}`}>
              <button onClick={() => toggle(n.id)} disabled={!hasReports}
                      className={`w-6 h-6 rounded flex items-center justify-center ${hasReports ? "hover:bg-stone-100" : "opacity-30"}`}
                      data-testid={`toggle-${n.id}`}>
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <img src={n.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{n.full_name}</div>
                <div className="text-xs text-stone-500">{n.designation} · {n.department_name}</div>
              </div>
              {hasReports && (
                <span className="status-pill" style={{ background: "var(--color-primary-light)", color: "var(--color-primary)" }}>
                  {n.reports.length} report{n.reports.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
